import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import ApiError from "@/model/ApiError";
import SignInLocationState from "@/model/SignInLocationState";
import "@/pages/SignUpPage/SignUpPage.scss";

function SignUpPage() {
  const { t } = useTranslation();
  const { isAuthenticated, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as SignInLocationState | null)?.from ?? "/";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t("auth.signUp.passwordMismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp(firstName, lastName, email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("auth.signUp.emailInUse"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(t("auth.signUp.validationError"));
      } else {
        setError(t("auth.signUp.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sign-up-page">
      <form className="sign-up-page__form" onSubmit={handleSubmit}>
        <h1>{t("auth.signUp.title")}</h1>

        <label className="sign-up-page__label" htmlFor="firstName">
          {t("auth.signUp.firstNameLabel")}
        </label>
        <input
          id="firstName"
          type="text"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          required
        />

        <label className="sign-up-page__label" htmlFor="lastName">
          {t("auth.signUp.lastNameLabel")}
        </label>
        <input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          required
        />

        <label className="sign-up-page__label" htmlFor="email">
          {t("auth.signUp.emailLabel")}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label className="sign-up-page__label" htmlFor="password">
          {t("auth.signUp.passwordLabel")}
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          minLength={8}
          required
        />

        <label className="sign-up-page__label" htmlFor="confirmPassword">
          {t("auth.signUp.confirmPasswordLabel")}
        </label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          minLength={8}
          required
        />

        {error && <p className="sign-up-page__error">{error}</p>}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("auth.signUp.submitting") : t("auth.signUp.submit")}
        </button>

        <p className="sign-up-page__switch">
          {t("auth.signUp.hasAccount")}{" "}
          <Link to="/signin" state={location.state}>{t("auth.signUp.signInLink")}</Link>
        </p>
      </form>
    </div>
  );
}

export default SignUpPage;
